import React, { Fragment, ReactElement, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DashboardLayout from "layout/DashboardLayout";
import { Button, Divider, Input, notification, Tag, Typography } from "antd";
import {
  TbSend,
  TbSend2,
  TbChevronCompactRight,
  TbChevronLeft,
  TbChevronCompactLeft,
  TbSparkles,
} from "react-icons/tb";
import { PROJECT_INFO } from "utils/constants";
import { Link, useParams } from "react-router-dom";
import { ApiService } from "api/api.service";
import { AI_ROLES, ApplicationSchemaDto } from "dtos/application.dto";
import { formatDate, formatDateTime, formatTime } from "utils/functions";
import { SingleQueryWrapper } from "components/QueryWrapper";
import { ReportSchemaDto } from "dtos/report.dto";
import dayjs from "dayjs";
import PageTitle from "components/PageTitle";

const ChatAndPreview = () => {
  const params = useParams();
  const appKey = params?.appKey;
  const reportId = params?.reportId;

  const report = ApiService.application.useGetReport(appKey, reportId);
  const fullApplication = ApiService.application.useGetByAppKey(appKey);
  const generateAIAuditReport = ApiService.application.useGenerateReport();

  if (!reportId || !appKey) {
    return <>Required params not found</>;
  }
  return (
    <DashboardLayout
      headerRight={
        <div className="flex gap-2 items-center">
          <Button
            type="primary"
            loading={generateAIAuditReport.isPending}
            onClick={() => {
              generateAIAuditReport.mutate(
                { app_key: appKey },
                {
                  onSuccess: async (data) => {
                    notification.info({ message: "Regeneration has started. Please return after 5 minutes." });
                  },
                },
              );
            }}
          >
            Regenerate Report
          </Button>
          <Button
            type="primary"
            onClick={() => {
              window.print();
            }}
          >
            Print
          </Button>
        </div>
      }
    >
      <PageTitle
        title={`AI Report`}
        pageTitle={[
          { title: `Applications`, link: "/dashboard/applications" },
          {
            title: fullApplication?.data?.data?.application?.client_details?.name ?? "Application Item",
            link: `/dashboard/application/${fullApplication?.data?.application?.app_key}`,
          },
          {
            title: "AI Report",
          },
        ]}
      />

      <SingleQueryWrapper
        query={report}
        renderData={(reportData) => {
          return (
            <SingleQueryWrapper
              query={fullApplication}
              renderData={(applicationData) => {
                return (
                  <ChatInternal
                    report={reportData}
                    adminApplication={applicationData.application}
                    reportId={reportId}
                  />
                );
              }}
            />
          );
        }}
      />
    </DashboardLayout>
  );
};

function ChatInternal({
  report,
  adminApplication,
  reportId,
}: {
  report: ReportSchemaDto;
  adminApplication: ApplicationSchemaDto;
  reportId: string;
}) {
  const [chatHide, setChatHide] = useState(false);

  let lastDate: string | undefined = undefined;

  return (
    <div className={`grid grid-cols-1 ${chatHide ? "lg:grid-cols-1" : "lg:grid-cols-[auto_450px]"}`} id="print_page">
      {/** Preview */}
      <div className="h-[calc(100vh-50px)] overflow-y-auto">
        <div className="prose prose-sm max-w-max p-4">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.body?.replace(/• /g, "- ")}</ReactMarkdown>
        </div>
        {/* {!chatHide ? (
            <TbChevronCompactRight
              onClick={() => setChatHide(true)}
              className="text-2xl absolute top-1/2 -right-6 cursor-pointer"
            />
          ) : (
            <TbChevronCompactLeft
              onClick={() => setChatHide(false)}
              className="text-2xl absolute top-1/2 -right-4 cursor-pointer"
            />
          )} */}
      </div>
      {/** Chat */}
      {!chatHide && (
        <div
          className="h-[calc(100vh-50px)] overflow-y-auto border-0 border-l border-solid border-gray-200"
          id="right_sider"
        >
          <div className={`flex flex-col bg-gray-100 h-full`}>
            <div className="flex-1 overflow-y-auto p-4">
              {adminApplication?.intelligence
                ?.find((el) => el.chat_history?.find((item) => item.report === reportId))
                ?.chat_history?.map((message, i) => {
                  const isSentByUser = message.role === AI_ROLES.USER;
                  if (!message.message) return null;
                  let renderData = null;
                  const timeComp = (
                    <p
                      className={`text-xs mt-1 opacity-60 text-right ${isSentByUser ? "text-gray-800" : "text-violet-950"}`}
                    >
                      {formatTime(message.timestamp)}
                    </p>
                  );
                  if (message.role === AI_ROLES.AI) {
                    renderData = (
                      <div className="bg-white p-2 rounded-lg" key={i}>
                        <Link to={`/dashboard/application/${adminApplication.app_key}/ai-audit/${message.report}`}>
                          <Button variant="solid" color="primary" icon={<TbSparkles />}>
                            {message.message}
                          </Button>
                        </Link>
                        {timeComp}
                      </div>
                    );
                  } else {
                    renderData = (
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          isSentByUser ? "bg-white text-gray-800" : " text-violet-950 bg-violet-200"
                        }`}
                      >
                        <p>{message.message}</p>
                        {timeComp}
                      </div>
                    );
                  }
                  let dividerData: string | undefined = undefined;
                  if (!lastDate) {
                    lastDate = message.timestamp;
                    dividerData = formatDate(message.timestamp);
                  } else {
                    const isSame = dayjs(message.timestamp).isSame(lastDate, "date");
                    if (!isSame) {
                      lastDate = message.timestamp;
                      dividerData = formatDate(message.timestamp);
                    }
                  }
                  return (
                    <Fragment key={i}>
                      {dividerData ? (
                        <Divider plain>
                          <Tag bordered={false} className="rounded-full bg-gray-200">
                            {dividerData}
                          </Tag>
                        </Divider>
                      ) : null}
                      <div key={i} className={`flex ${isSentByUser === false ? "justify-start" : "justify-end"} mb-4`}>
                        {renderData}
                      </div>
                    </Fragment>
                  );
                })}
            </div>
            <div className="p-3 bg-white border-solid border-0 border-t border-gray-200 flex gap-2 items-center">
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 11 }}
                placeholder="Type here..."
                style={{ width: "100%" }}
              />
              <Button variant="solid" color="primary" icon={<TbSend2 size={20} />} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatAndPreview;
